import { CalendarIcon, CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { DateTime } from "luxon";
import Modal from "../Shared/Modal";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import { APPOINTMENT_STATUS } from "../../config/appointment";

const PendingApprovalsModal = ({
  open,
  onClose,
  appointments = [],
  onApprove,
  onDecline,
  onView,
  onEdit,
  onClientClick,
}) => {
  const { is12HourClock } = useUserPreferences();
  const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const now = DateTime.now();
  const upcomingAppointments = appointments.filter(
    (a) => DateTime.fromISO(a.startTime) > now
  );
  const pastAppointments = appointments.filter(
    (a) => DateTime.fromISO(a.startTime) <= now
  );

  const formatTime = (isoTime) => {
    if (!isoTime) return "";
    const format = is12HourClock ? "MMM d, h:mm a" : "MMM d, HH:mm";
    return DateTime.fromISO(isoTime).setZone(systemTimezone).toFormat(format);
  };

  const getRelativeTime = (isoTime) => {
    if (!isoTime) return "";
    return DateTime.fromISO(isoTime).toRelative();
  };

  const buildViewPayload = (appointment) => {
    const statusObj = APPOINTMENT_STATUS.find(
      (x) => x.id === appointment.status
    );
    return {
      id: appointment.id,
      ...appointment,
      startTime: formatTime(appointment.startTime),
      endTime: formatTime(appointment.endTime),
      status: statusObj?.name || "Pending",
      statusColour: statusObj?.colour || "YELLOW",
      address: appointment.serviceAddress?.full,
      clientName: appointment.clientFullName,
    };
  };

  const handleRowClick = (e, appointment) => {
    onView(buildViewPayload(appointment));
  };

  return (
    <Modal open={open} onClose={onClose} title="Pending Approvals" size="md">
      {appointments.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-ink-500">No pending appointments</p>
        </div>
      ) : (
        <>
          {/* Upcoming section */}
          {upcomingAppointments.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-500 mb-3">
                Upcoming ({upcomingAppointments.length})
              </h4>
              <div className="divide-y divide-ink-100">
                {upcomingAppointments.map((appointment) => (
                  <div
                    key={appointment.id}
                    className="flex items-start justify-between gap-3 py-3 first:pt-0 cursor-pointer rounded-md -mx-2 px-2 hover:bg-cream-100 transition-colors"
                    onClick={(e) => handleRowClick(e, appointment)}
                  >
                    <div className="min-w-0 flex-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onClientClick(appointment.clientId);
                        }}
                        className="text-sm font-medium text-ink-900 hover:text-accent-500 hover:underline transition-colors duration-150 truncate text-left"
                      >
                        {appointment.clientFullName}
                      </button>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-ink-500">
                        <span className="flex items-center gap-x-1">
                          <CalendarIcon className="size-3.5 text-ink-400" />
                          {formatTime(appointment.startTime)}
                          <span className="text-ink-400">
                            ({getRelativeTime(appointment.startTime)})
                          </span>
                        </span>
                        {appointment.services?.length > 0 && (
                          <span>
                            {appointment.services
                              .map((s) => s.name)
                              .join(", ")}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0 pt-0.5">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onApprove(appointment.id);
                        }}
                        className="inline-flex items-center gap-1 rounded-md bg-signal-green-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-signal-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
                      >
                        <CheckIcon className="size-3.5" />
                        Approve
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDecline(appointment.id);
                        }}
                        className="rounded-md p-1.5 text-ink-400 hover:text-signal-red-600 hover:bg-signal-red-50 transition-colors"
                        title="Decline"
                      >
                        <XMarkIcon className="size-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Past section */}
          {pastAppointments.length > 0 && (
            <div
              className={
                upcomingAppointments.length > 0
                  ? "mt-6 pt-4 border-t border-ink-100"
                  : ""
              }
            >
              <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-500 mb-3">
                Past ({pastAppointments.length})
              </h4>
              <div className="divide-y divide-ink-100">
                {pastAppointments.map((appointment) => (
                  <div
                    key={appointment.id}
                    className="flex items-start justify-between gap-3 py-3 first:pt-0 cursor-pointer rounded-md -mx-2 px-2 hover:bg-cream-100 transition-colors"
                    onClick={(e) => handleRowClick(e, appointment)}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onClientClick(appointment.clientId);
                          }}
                          className="text-sm font-medium text-ink-500 hover:text-accent-500 hover:underline transition-colors duration-150 truncate text-left"
                        >
                          {appointment.clientFullName}
                        </button>
                        <span className="inline-flex items-center rounded bg-cream-200 px-1.5 py-0.5 text-[11px] font-medium text-ink-500">
                          Date passed
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-ink-500">
                        <span className="flex items-center gap-x-1">
                          <CalendarIcon className="size-3.5 text-ink-400" />
                          {formatTime(appointment.startTime)}
                        </span>
                        {appointment.services?.length > 0 && (
                          <span>
                            {appointment.services
                              .map((s) => s.name)
                              .join(", ")}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center flex-shrink-0 pt-0.5">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDecline(appointment.id, { isPast: true });
                        }}
                        className="rounded-md p-1.5 text-ink-400 hover:text-signal-red-600 hover:bg-signal-red-50 transition-colors"
                        title="Decline"
                      >
                        <XMarkIcon className="size-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default PendingApprovalsModal;
